/**
 * Read a workspace file as TEXT, with absent kept distinct from unreadable.
 *
 * `useFileBytes` hands back a Blob URL, which is what an `<iframe>`/`<img>`
 * wants and not what a markdown renderer wants. This wraps it: decode to a
 * string, and collapse the several ways a read can go wrong into ONE state
 * machine callers can switch on.
 *
 * ## The four states, and why "absent" is not "unreadable"
 *
 * A file that is NOT THERE and a file we COULD NOT READ are different answers,
 * and rendering them the same way is a lie that reads as reassurance: "there is
 * no deeper analysis" when the truth is "I could not look". The gateway says
 * which by CODE (`NOT_FOUND`), never by localized prose.
 *
 * `loading` · `absent` · `unreadable` · `present`
 *
 * ⚠ `MarkdownHtmlViewer` deliberately still does its own decode. It carries
 * concerns this hook has no business in (annotation stripping, scroll restore
 * keyed on the decoded source), and migrating it is a refactor of a viewer that
 * has already been fixed twice today — not something to fold into a feature.
 */

import { useEffect, useState } from "react";
import { useFileBytes } from "@/hooks/useFileBytes";
import { GatewayRpcError } from "@/lib/gateway-client";

export type FileTextState = "loading" | "absent" | "unreadable" | "present";

export interface UseFileTextResult {
  state: FileTextState;
  /** The decoded text — non-null only when `state === "present"`. */
  text: string | null;
}

/** @param path the file to read, or null when there is nothing to ask for. */
export function useFileText(path: string | null): UseFileTextResult {
  const { url, isPending, isError, error } = useFileBytes(path);
  const [text, setText] = useState<string | null>(null);
  const [decodeError, setDecodeError] = useState<Error | null>(null);

  useEffect(() => {
    if (!url) {
      setText(null);
      setDecodeError(null);
      return;
    }
    let cancelled = false;
    setText(null);
    setDecodeError(null);
    // Re-fetching the Blob URL is cheap — the browser serves it from memory.
    fetch(url)
      .then((r) => r.text())
      .then((t) => {
        if (!cancelled) setText(t);
      })
      .catch((err) => {
        // ⚠ A decode failure is UNREADABLE, not absent. Letting it resolve to
        // "no text" would turn a broken read into a confident nothing.
        if (!cancelled) setDecodeError(err as Error);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  // ⚠ Nothing was ASKED FOR. Reported as `absent` because every caller's
  // handling is identical (render nothing), but it is not evidence the file is
  // missing — a caller that must tell "no report" from "did not look" should
  // not pass null and then read this answer as the former.
  if (!path) return { state: "absent", text: null };
  if (decodeError) return { state: "unreadable", text: null };
  if (isError) {
    const notFound = error instanceof GatewayRpcError && error.code === "NOT_FOUND";
    return { state: notFound ? "absent" : "unreadable", text: null };
  }
  if (isPending) return { state: "loading", text: null };
  // ⚠ NOT `unreadable`. `useFileBytes` mints its Blob URL inside an EFFECT, so
  // there is a render on the happy path where the query has settled, there is
  // no error, and `url` is still null — calling that unreadable flashes an
  // alarm on every successful read. A genuine decode failure cannot land here:
  // that hook folds it into `isError`, which is handled above.
  if (!url) return { state: "loading", text: null };
  if (text === null) return { state: "loading", text: null };
  return { state: "present", text };
}
